import { DateTime } from "luxon";
import { TimeOfDay, TimeOfDayRegex } from "./TimeOfDay";
import { Weekday, WeekdayEnum } from "./Weekday";
import { z } from "zod";
import { Timestamp } from "firebase/firestore";

export const DateRegex =
  /^(19|20)[0-9]{2}-((0[13578]|1[02])-(0[1-9]|[12][0-9]|3[01])|(0[469]|11)-(0[1-9]|[12][0-9]|30)|(02)-(0[1-9]|[12][0-9]))$/;

export class DateWithoutTime {
  static today(): DateWithoutTime {
    return DateWithoutTime.fromDateTime(DateTime.now().setZone("Europe/Berlin"));
  }

  static tomorrow(): DateWithoutTime {
    return DateWithoutTime.today().nextDay;
  }

  static fromDateTime(dateTime: DateTime): DateWithoutTime {
    if (!dateTime.isValid) {
      throw new Error(`Given dateTime is not valid: ${dateTime.invalidExplanation}`);
    }
    return new DateWithoutTime(dateTime.year, dateTime.month, dateTime.day);
  }

  /**
   * @param date must be midnight in the time zone Europe/Berlin
   */
  static fromDate(date: Date): DateWithoutTime {
    const dateTime = DateTime.fromJSDate(date, { zone: "Europe/Berlin" });
    if (dateTime.hour !== 0 || dateTime.minute !== 0 || dateTime.second !== 0 || dateTime.millisecond !== 0) {
      throw new Error("The given date does not represent midnight in the time zone Europe/Berlin");
    }
    return new DateWithoutTime(dateTime.year, dateTime.month, dateTime.day);
  }

  /**
   * @param dateString in the format "yyyy-MM-dd"
   */
  static fromString(dateString: string): DateWithoutTime {
    if (!DateRegex.test(dateString)) {
      throw new Error(`Invalid dateString: ${JSON.stringify(dateString)} -- must be in the format "yyyy-MM-dd"`);
    }
    return DateWithoutTime.fromDateTime(DateTime.fromISO(dateString));
  }

  /**
   * @param dateNumber year * 10000 + month * 100 + day
   */
  static fromNumber(dateNumber: number): DateWithoutTime {
    const year = Math.floor(dateNumber / 10000);
    const month = Math.floor((dateNumber % 10000) / 100);
    const day = dateNumber % 100;
    const dateTime = DateTime.fromObject({ year, month, day });
    if (dateTime.year !== year || dateTime.month !== month || dateTime.day !== day) {
      throw new Error(`Invalid dateNumber: ${dateNumber}`);
    }
    return new DateWithoutTime(year, month, day);
  }

  private _dateTime: DateTime;

  constructor(
    public year: number,
    public month: number,
    public day: number,
  ) {
    const dateTime = DateTime.fromObject({ year, month, day }, { zone: "Europe/Berlin" });
    if (!dateTime.isValid || dateTime.year !== year || dateTime.month !== month || dateTime.day !== day) {
      throw new Error(`Invalid date: ${year}-${month}-${day}`);
    }
    this._dateTime = dateTime;
  }

  get prevDay(): DateWithoutTime {
    return DateWithoutTime.fromDateTime(this.toDateTime().minus({ hours: 5 }));
  }

  get nextDay(): DateWithoutTime {
    return DateWithoutTime.fromDateTime(this.toDateTime().plus({ hours: 29 }));
  }

  get startOfMonth(): DateWithoutTime {
    return new DateWithoutTime(this.year, this.month, 1);
  }

  get endOfMonth(): DateWithoutTime {
    let d = new DateWithoutTime(this.year, this.month, 28);
    let nextDay = d.nextDay;
    while (this.month === nextDay.month) {
      d = nextDay;
      nextDay = d.nextDay;
    }
    return d;
  }

  get weekday(): Weekday {
    return WeekdayEnum.options[this.toDateTime().get("weekday") - 1];
  }

  get weekNumber(): number {
    return this.toDateTime().weekNumber;
  }

  addDays(n: number): DateWithoutTime {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let date: DateWithoutTime = this;
    for (let i = 0; i < n; ++i) {
      date = date.nextDay;
    }
    return date;
  }

  valueOf() {
    return this.year * 10000 + this.month * 100 + this.day;
  }

  toInt() {
    return this.valueOf();
  }

  toString() {
    return this.toDateTime().toISODate();
  }

  toLocalizedString() {
    return `${this.day}.${this.month}.${this.year}`;
  }

  toDate() {
    return this.toDateTime().toJSDate();
  }

  toDateTime(): DateTime {
    return this._dateTime;
  }

  at(timeOfDayOrHour: TimeOfDay | number, minute: number = 0, second: number = 0): DateTime {
    if (typeof timeOfDayOrHour === "string") {
      const match = TimeOfDayRegex.exec(timeOfDayOrHour);
      if (!match) {
        return DateTime.invalid(`invalid timeOfDayOrHour: ${JSON.stringify(timeOfDayOrHour)}`);
      }
      return this.toDateTime().set({ hour: parseInt(match[1], 10), minute: parseInt(match[2], 10), second });
    }
    return this.toDateTime().set({ hour: timeOfDayOrHour, minute, second });
  }
}

export const DateWithoutTimeSchema = z.any().transform((val) => {
  if (typeof val === "string") {
    return DateWithoutTime.fromString(val);
  } else if (typeof val === "number") {
    return DateWithoutTime.fromNumber(val);
  } else if (val instanceof DateWithoutTime) {
    return val;
  } else if (val instanceof Date) {
    return DateWithoutTime.fromDate(val);
  } else if (val instanceof DateTime) {
    return DateWithoutTime.fromDateTime(val);
  } else if (val instanceof Timestamp) {
    return DateWithoutTime.fromDate(val.toDate());
  } else {
    throw new Error(`Invalid value: ${JSON.stringify(val)}`);
  }
});
